"use client";

/**
 * Barra de progreso fina arriba al navegar entre páginas.
 *
 * Reemplaza a los `loading.tsx` públicos: esos archivos hacían que TODA página
 * se mandara primero como "Cargando…" + footer y el contenido llegara después
 * por streaming. Eso daba CLS alto, LCP tardío, 404 que respondían 200 y
 * redirects como 200 + meta refresh (malo para SEO). Sin ellos el HTML llega
 * completo; esta barra conserva la señal visual de "cargando" al hacer clic.
 */
import { useEffect, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";

export function NavProgress() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [active, setActive] = useState(false);
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Llegó la página nueva: se oculta la barra.
  useEffect(() => {
    setActive(false); // eslint-disable-line react-hooks/set-state-in-effect
    if (timeout.current) clearTimeout(timeout.current);
  }, [pathname, searchParams]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as Element | null)?.closest?.("a");
      if (!a || a.target === "_blank" || a.hasAttribute("download")) return;
      const href = a.getAttribute("href");
      if (!href || href.startsWith("#")) return;
      let url: URL;
      try {
        url = new URL(href, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;
      setActive(true);
      if (timeout.current) clearTimeout(timeout.current);
      // Red de seguridad: nunca se queda pegada.
      timeout.current = setTimeout(() => setActive(false), 10_000);
    }
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-x-0 top-0 z-[100000] h-0.5 overflow-hidden"
      style={{ opacity: active ? 1 : 0, transition: "opacity 200ms" }}
    >
      {active ? <div className="h-full w-1/3 bg-primary animate-[sucom-nav_1.1s_ease-in-out_infinite]" /> : null}
    </div>
  );
}
